import { useQuery, useMutation } from "react-query";
import { getClients, addClient, deleteClient, sendManualCoupon } from "../../../service";
import {
  Table,
  Input,
  Button,
  Modal,
  Form,
  message,
  Space,
  Popconfirm,
  Tag,
  Descriptions,
  Statistic,
  Row,
  Col,
  Card,
  Timeline,
  Typography,
  Divider,
  Select,
  InputNumber,
} from "antd";
import { useState, useMemo, useRef, useCallback } from "react";
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  GiftOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { colors, spacing, shadows } from "../../../styles/theme";

const { Text } = Typography;

const StatCard = styled(Card)`
  border-radius: 10px !important;
  box-shadow: ${shadows.small} !important;
  .ant-statistic-title {
    font-size: 12px;
    color: ${colors.textSecondary};
  }
  .ant-statistic-content {
    font-size: 20px;
  }
`;

const OrderTimeline = styled.div`
  max-height: 350px;
  overflow-y: auto;
  padding-right: 8px;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.borderLight};
    border-radius: 4px;
  }
`;

const STATUS_MAP: Record<string, { labelKey: string; color: string }> = {
  DONE: { labelKey: "statusDone", color: "green" },
  IN_QUEUE: { labelKey: "statusInQueue", color: "blue" },
  IN_PROGRESS: { labelKey: "statusInProgress", color: "orange" },
  CANCELLED: { labelKey: "statusCancelled", color: "red" },
  WAITING_FOR_PAYMENT: { labelKey: "statusWaitingForPayment", color: "gold" },
  READY: { labelKey: "statusReady", color: "cyan" },
};

const formatPrice = (cents: number) => `${(cents / 100).toFixed(2)} zł`;

export const MarketingClients: React.FC = () => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchText(value), 300);
  }, []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailsClient, setDetailsClient] = useState<any>(null);
  const [form] = Form.useForm();

  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);
  const [couponForm] = Form.useForm();
  const [selectedClientForCoupon, setSelectedClientForCoupon] = useState<any>(null);

  const {
    data: companyClientsData,
    isLoading,
    refetch,
  } = useQuery<any, Error>(["/clients"], () => getClients());

  const addClientMutation = useMutation(addClient, {
    onSuccess: () => {
      message.success(t("marketingClients.clientAddedSuccess"));
      refetch();
      setIsModalOpen(false);
      form.resetFields();
    },
    onError: () => {
      message.error(t("marketingClients.clientAddError"));
    },
  });

  const deleteClientMutation = useMutation(deleteClient, {
    onSuccess: () => {
      message.success(t("marketingClients.clientDeletedSuccess"));
      refetch();
    },
    onError: () => {
      message.error(t("marketingClients.clientDeleteError"));
    },
  });

  const sendCouponMutation = useMutation(sendManualCoupon, {
    onSuccess: () => {
      message.success(t("marketingClients.couponSentSuccess"));
      setIsCouponModalOpen(false);
      couponForm.resetFields();
      setSelectedClientForCoupon(null);
    },
    onError: (err: any) => {
      console.error(err);
      message.error(t("marketingClients.couponSendError"));
    }
  });

  const handleOpenCouponModal = (client: any) => {
    setSelectedClientForCoupon(client);
    setIsCouponModalOpen(true);
    couponForm.setFieldsValue({
      channel: "SMS",
      couponType: "PERCENTAGE",
      couponValue: 10,
      codePrefix: "PREZENT",
      // Basic default template using placeholders
      messageTemplate: t("marketingClients.defaultMessageTemplate"),
      emailSubject: t("marketingClients.defaultEmailSubject")
    });
  };

  const handleSendCouponSubmit = (values: any) => {
    if (!selectedClientForCoupon) return;
    // Convert value to cents if needed, but backend seems to handle raw value
    // Wait, backend expects value in cents for proper storage usually, or raw percentage
    // If FIXED_AMOUNT, convert to cents. If PERCENTAGE, keep as is.

    let val = values.couponValue;
    if (values.couponType === 'FIXED_AMOUNT') {
      val = Math.round(val * 100);
    }

    sendCouponMutation.mutate({
      clientId: selectedClientForCoupon.id,
      ...values,
      couponValue: val
    });
  };

  const filteredData = useMemo(() => {
    if (!searchText || !companyClientsData) return companyClientsData;

    const lowerSearchText = searchText.toLowerCase();

    return companyClientsData.filter((client: any) => {
      return (
        (client.name?.toLowerCase() || "").includes(lowerSearchText) ||
        (client.email?.toLowerCase() || "").includes(lowerSearchText) ||
        (client.phoneNumber?.toLowerCase() || "").includes(lowerSearchText)
      );
    });
  }, [companyClientsData, searchText]);

  const handleAddClient = (values: any) => {
    addClientMutation.mutate(values);
  };

  const handleDeleteClient = (id: string) => {
    deleteClientMutation.mutate(id);
  };

  // Compute average order value for detail modal
  const avgOrderValue = useMemo(() => {
    if (!detailsClient || !detailsClient.totalOrders) return 0;
    return detailsClient.totalSpent / detailsClient.totalOrders;
  }, [detailsClient]);

  const columns = [
    {
      title: t("marketingClients.columnName"),
      dataIndex: "name",
      key: "name",
      sorter: (a: any, b: any) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: t("marketingClients.columnEmail"),
      dataIndex: "email",
      key: "email",
      ellipsis: true,
    },
    {
      title: t("marketingClients.columnPhoneNumber"),
      dataIndex: "phoneNumber",
      key: "phoneNumber",
    },
    {
      title: t("marketingClients.columnOrders"),
      dataIndex: "totalOrders",
      key: "totalOrders",
      sorter: (a: any, b: any) => (a.totalOrders || 0) - (b.totalOrders || 0),
      render: (val: number) => (
        <Tag color={val > 5 ? "green" : val > 0 ? "blue" : "default"}>
          {val || 0}
        </Tag>
      ),
      width: 110,
    },
    {
      title: t("marketingClients.columnSpent"),
      dataIndex: "totalSpent",
      key: "totalSpent",
      sorter: (a: any, b: any) => (a.totalSpent || 0) - (b.totalSpent || 0),
      render: (val: number) => formatPrice(val || 0),
      width: 120,
    },
    {
      title: t("marketingClients.columnLastOrder"),
      dataIndex: "lastOrderDate",
      key: "lastOrderDate",
      sorter: (a: any, b: any) => {
        if (!a.lastOrderDate) return -1;
        if (!b.lastOrderDate) return 1;
        return dayjs(a.lastOrderDate).unix() - dayjs(b.lastOrderDate).unix();
      },
      render: (val: string | null) =>
        val ? (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {dayjs(val).format("DD.MM.YYYY")}
          </Text>
        ) : (
          "—"
        ),
      width: 130,
    },
    {
      title: t("marketingClients.columnActions"),
      key: "actions",
      width: 160,
      render: (_: any, record: any) => (
        <Space size="small">
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => setDetailsClient(record)}
          >
            {t("marketingClients.detailsButton")}
          </Button>
          <Button
            icon={<GiftOutlined />}
            size="small"
            onClick={() => handleOpenCouponModal(record)}
            title={t("marketingClients.sendDedicatedCouponTitle")}
          />
          <Popconfirm
            title={t("marketingClients.deleteConfirmTitle")}
            onConfirm={() => handleDeleteClient(record.id)}
            okText={t("marketingClients.yes")}
            cancelText={t("marketingClients.no")}
          >
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space
        style={{
          marginBottom: 16,
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <Input
          placeholder={t("marketingClients.searchPlaceholder")}
          allowClear
          onChange={handleSearchChange}
          style={{ width: 400 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsModalOpen(true)}
        >
          {t("marketingClients.addClientButton")}
        </Button>
      </Space>
      <Table
        virtual
        dataSource={filteredData}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: 900 }}
      />

      {/* ========= ADD CLIENT MODAL ========= */}
      <Modal
        title={t("marketingClients.addClientModalTitle")}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={addClientMutation.isLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleAddClient}>
          <Form.Item
            label={t("marketingClients.formNameLabel")}
            name="name"
            rules={[{ required: true, message: t("marketingClients.formNameRequired") }]}
          >
            <Input placeholder={t("marketingClients.formNamePlaceholder")} />
          </Form.Item>
          <Form.Item
            label={t("marketingClients.formEmailLabel")}
            name="email"
            rules={[
              { required: true, message: t("marketingClients.formEmailRequired") },
              {
                type: "email",
                message: t("marketingClients.formEmailInvalid"),
              },
            ]}
          >
            <Input placeholder={t("marketingClients.formEmailPlaceholder")} />
          </Form.Item>
          <Form.Item
            label={t("marketingClients.formPhoneLabel")}
            name="phoneNumber"
            rules={[
              {
                required: true,
                message: t("marketingClients.formPhoneRequired"),
              },
            ]}
          >
            <Input placeholder={t("marketingClients.formPhonePlaceholder")} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ========= COUPON MODAL ========= */}
      <Modal
        title={t("marketingClients.couponModalTitle", { name: selectedClientForCoupon?.name || "" })}
        open={isCouponModalOpen}
        onCancel={() => setIsCouponModalOpen(false)}
        onOk={() => couponForm.submit()}
        confirmLoading={sendCouponMutation.isLoading}
        width={600}
      >
        <Form form={couponForm} layout="vertical" onFinish={handleSendCouponSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="channel" label={t("marketingClients.couponChannelLabel")} rules={[{ required: true, message: t("marketingClients.couponChannelRequired") }]}>
                <Select>
                  <Select.Option value="SMS">SMS</Select.Option>
                  <Select.Option value="EMAIL">E-mail</Select.Option>
                  {/* <Select.Option value="BOTH">Oba</Select.Option> */}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="codePrefix" label={t("marketingClients.couponCodePrefixLabel")} rules={[{ required: true, message: t("marketingClients.couponCodePrefixRequired") }]}>
                <Input placeholder={t("marketingClients.couponCodePrefixPlaceholder")} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="couponType" label={t("marketingClients.couponTypeLabel")} rules={[{ required: true }]}>
                <Select onChange={() => couponForm.setFieldsValue({ couponValue: null })}>
                  <Select.Option value="PERCENTAGE">{t("marketingClients.couponTypePercentage")}</Select.Option>
                  <Select.Option value="FIXED_AMOUNT">{t("marketingClients.couponTypeFixedAmount")}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) => prev.couponType !== curr.couponType}
              >
                {({ getFieldValue }) => {
                  const type = getFieldValue('couponType');
                  const label = type === 'PERCENTAGE' ? t("marketingClients.couponValuePercentLabel") : t("marketingClients.couponValueAmountLabel");
                  return (
                    <Form.Item name="couponValue" label={label} rules={[{ required: true, message: t("marketingClients.couponValueRequired") }]}>
                      <InputNumber style={{ width: '100%' }} min={1} />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.channel !== curr.channel}
          >
            {({ getFieldValue }) => {
              const channel = getFieldValue('channel');
              return channel === 'EMAIL' ? (
                <Form.Item name="emailSubject" label={t("marketingClients.emailSubjectLabel")} rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              ) : null;
            }}
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.channel !== curr.channel}
          >
            {({ getFieldValue }) => {
              const channel = getFieldValue('channel');
              return (
                <Form.Item name="messageTemplate" label={t("marketingClients.messageTemplateLabel")} help={t("marketingClients.messageTemplateHelp")} rules={[{ required: true }]}>
                  <Input.TextArea rows={4} maxLength={channel === 'SMS' ? 320 : undefined} showCount={channel === 'SMS'} />
                </Form.Item>
              );
            }}
          </Form.Item>

          {selectedClientForCoupon && (
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("marketingClients.couponInfoText", { channel: couponForm.getFieldValue('channel') })}
                <br />
                {t("marketingClients.couponContactText", { phone: selectedClientForCoupon.phoneNumber, email: selectedClientForCoupon.email })}
              </Text>
            </div>
          )}
        </Form>
      </Modal>

      {/* ========= CLIENT DETAILS MODAL ========= */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>{t("marketingClients.detailsModalTitle", { name: detailsClient?.name || "—" })}</span>
          </Space>
        }
        open={!!detailsClient}
        onCancel={() => setDetailsClient(null)}
        footer={
          <Button onClick={() => setDetailsClient(null)}>{t("marketingClients.closeButton")}</Button>
        }
        width={700}
      >
        {detailsClient && (
          <>
            {/* Client info */}
            <Descriptions
              bordered
              size="small"
              column={{ xs: 1, sm: 2 }}
              style={{ marginBottom: 20 }}
            >
              <Descriptions.Item label={t("marketingClients.descNameLabel")}>
                {detailsClient.name || "—"}
              </Descriptions.Item>
              <Descriptions.Item label={t("marketingClients.descEmailLabel")}>
                {detailsClient.email || "—"}
              </Descriptions.Item>
              <Descriptions.Item label={t("marketingClients.descPhoneLabel")}>
                {detailsClient.phoneNumber || "—"}
              </Descriptions.Item>
              <Descriptions.Item label={t("marketingClients.descClientSinceLabel")}>
                {detailsClient.firstOrderDate
                  ? dayjs(detailsClient.firstOrderDate).format("DD.MM.YYYY")
                  : "—"}
              </Descriptions.Item>
            </Descriptions>

            {/* KPI cards */}
            <Row gutter={12} style={{ marginBottom: 20 }}>
              <Col xs={12} sm={6}>
                <StatCard size="small">
                  <Statistic
                    title={t("marketingClients.statOrders")}
                    value={detailsClient.totalOrders || 0}
                    prefix={<ShoppingCartOutlined />}
                  />
                </StatCard>
              </Col>
              <Col xs={12} sm={6}>
                <StatCard size="small">
                  <Statistic
                    title={t("marketingClients.statTotalSpent")}
                    value={formatPrice(detailsClient.totalSpent || 0)}
                    prefix={<DollarOutlined />}
                  />
                </StatCard>
              </Col>
              <Col xs={12} sm={6}>
                <StatCard size="small">
                  <Statistic
                    title={t("marketingClients.statAvgOrder")}
                    value={formatPrice(avgOrderValue)}
                    prefix={<DollarOutlined />}
                  />
                </StatCard>
              </Col>
              <Col xs={12} sm={6}>
                <StatCard size="small">
                  <Statistic
                    title={t("marketingClients.statLastOrder")}
                    value={
                      detailsClient.lastOrderDate
                        ? dayjs(detailsClient.lastOrderDate).format(
                          "DD.MM.YYYY"
                        )
                        : "—"
                    }
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ fontSize: 16 }}
                  />
                </StatCard>
              </Col>
            </Row>

            {/* Order history */}
            <Divider orientation="left" style={{ fontSize: 14 }}>
              <CalendarOutlined /> {t("marketingClients.orderHistoryTitle")}
            </Divider>

            {detailsClient.orders && detailsClient.orders.length > 0 ? (
              <OrderTimeline>
                <Timeline
                  items={detailsClient.orders.map((order: any) => {
                    const stMap = STATUS_MAP[order.status];
                    const st = {
                      label: stMap ? t(`marketingClients.${stMap.labelKey}`) : order.status,
                      color: stMap ? stMap.color : "default",
                    };
                    return {
                      key: order.id,
                      color: st.color,
                      children: (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <Text style={{ fontSize: 13 }}>
                              {dayjs(order.createdAt).format(
                                "DD.MM.YYYY HH:mm"
                              )}
                            </Text>
                            <br />
                            <Tag color={st.color} style={{ marginTop: 4 }}>
                              {st.label}
                            </Tag>
                          </div>
                          <Text strong style={{ fontSize: 14 }}>
                            {formatPrice(order.price)}
                          </Text>
                        </div>
                      ),
                    };
                  })}
                />
              </OrderTimeline>
            ) : (
              <Text type="secondary">{t("marketingClients.noOrders")}</Text>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};
